import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { challengesAPI } from '../../utils/api';
import AdminHeader from './AdminHeader';
import { Drawer } from '../shared/DrawerManager';
import './ChallengeManagement.css';

const ChallengeManagement = forwardRef(({ onNavigate, currentView, embedded = false }, ref) => {
  const { isAdmin } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [filteredChallenges, setFilteredChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const challengesPerPage = 10;

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', or 'preview'
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [challengeToDelete, setChallengeToDelete] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'web',
    difficulty: 'junior',
    totalPoints: 100,
    timeLimit: 60,
    dockerImage: '',
    scenario: '',
  });

  useEffect(() => {
    if (!isAdmin()) {
      setError('Access denied. Admin privileges required.');
      return;
    }
    loadChallenges();
  }, []);

  useEffect(() => {
    // Apply filters
    let filtered = challenges;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(challenge =>
        challenge.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        challenge.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Difficulty filter
    if (difficultyFilter !== 'all') {
      filtered = filtered.filter(challenge => challenge.difficulty === difficultyFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(challenge => challenge.category === categoryFilter);
    }

    setFilteredChallenges(filtered);
    setCurrentPage(1);
  }, [challenges, searchTerm, difficultyFilter, categoryFilter]);

  const loadChallenges = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await challengesAPI.getAll();
      setChallenges(response.data.challenges || []);
    } catch (err) {
      console.error('Failed to load challenges:', err);
      setError(err.response?.data?.error || 'Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    handleCreate: handleCreateChallenge
  }));

  const handleCreateChallenge = () => {
    setModalMode('create');
    setCurrentChallenge(null);
    setFormData({
      title: '',
      description: '',
      category: 'web',
      difficulty: 'junior',
      totalPoints: 100,
      timeLimit: 60,
      dockerImage: '',
      scenario: '',
    });
    setShowModal(true);
  };

  const handleEditChallenge = (challenge) => {
    setModalMode('edit');
    setCurrentChallenge(challenge);
    setFormData({
      title: challenge.title,
      description: challenge.description || '',
      category: challenge.category,
      difficulty: challenge.difficulty,
      totalPoints: challenge.totalPoints,
      timeLimit: challenge.timeLimit || 60,
      dockerImage: challenge.dockerImage || '',
      scenario: challenge.scenario || '',
    });
    setShowModal(true);
  };

  const handlePreviewChallenge = (challenge) => {
    setModalMode('preview');
    setCurrentChallenge(challenge);
    setShowModal(true);
  };

  const handleDeleteClick = (challenge) => {
    setChallengeToDelete(challenge);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setError(null);
      await challengesAPI.delete(challengeToDelete._id);
      setSuccess(`Challenge "${challengeToDelete.title}" deleted successfully`);
      setShowDeleteConfirm(false);
      setChallengeToDelete(null);
      loadChallenges();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to delete challenge:', err);
      setError(err.response?.data?.error || 'Failed to delete challenge');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      if (modalMode === 'create') {
        await challengesAPI.create(formData);
        setSuccess('Challenge created successfully');
      } else {
        await challengesAPI.update(currentChallenge._id, formData);
        setSuccess('Challenge updated successfully');
      }

      setShowModal(false);
      loadChallenges();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to save challenge:', err);
      setError(err.response?.data?.error || 'Failed to save challenge');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  // Pagination
  const indexOfLastChallenge = currentPage * challengesPerPage;
  const indexOfFirstChallenge = indexOfLastChallenge - challengesPerPage;
  const currentChallenges = filteredChallenges.slice(indexOfFirstChallenge, indexOfLastChallenge);
  const totalPages = Math.ceil(filteredChallenges.length / challengesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (!isAdmin()) {
    return (
      <div className="challenge-management">
        <div className="error-state">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="challenge-management">
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Loading challenges...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="challenge-management">
      {!embedded && <AdminHeader currentView={currentView} onNavigate={onNavigate} />}
      <div className="challenge-management-content">
        {!embedded && <div className="management-header">
          <h2>Challenge Management</h2>
          <button className="cyber-btn success" onClick={handleCreateChallenge}>
            + Create Challenge
          </button>
        </div>}

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {success && (
          <div className="success-banner">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)}>×</button>
          </div>
        )}

      {/* Filters */}
      <div className="filters-section terminal-card">
        <div className="terminal-header">
          <div className="terminal-dots">
            <span className="terminal-dot red"></span>
            <span className="terminal-dot yellow"></span>
            <span className="terminal-dot green"></span>
          </div>
          <span className="terminal-title">filters</span>
        </div>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              className="cyber-input"
              placeholder="Search by title or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Category</label>
            <select
              className="cyber-input"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All Categories</option>
              <option value="web">Web Security</option>
              <option value="api">API Security</option>
              <option value="network">Network Security</option>
              <option value="binary">Binary Exploitation</option>
              <option value="mixed">Mixed</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Difficulty</label>
            <select
              className="cyber-input"
              value={difficultyFilter}
              onChange={(e) => setDifficultyFilter(e.target.value)}
            >
              <option value="all">All Difficulties</option>
              <option value="junior">Junior</option>
              <option value="senior">Senior</option>
              <option value="advanced">Advanced</option>
              <option value="redteam">Red Team</option>
            </select>
          </div>
        </div>
      </div>

      {/* Challenges Table */}
      <div className="challenges-table-section terminal-card">
        <div className="terminal-header">
          <div className="terminal-dots">
            <span className="terminal-dot red"></span>
            <span className="terminal-dot yellow"></span>
            <span className="terminal-dot green"></span>
          </div>
          <span className="terminal-title">challenges.list</span>
        </div>
        <div className="table-wrapper">
          {currentChallenges.length === 0 ? (
            <div className="empty-state">
              <p>No challenges found</p>
            </div>
          ) : (
            <table className="cyber-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Difficulty</th>
                  <th>Points</th>
                  <th>Time Limit</th>
                  <th>Status</th>
                  <th>Availability</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentChallenges.map((challenge) => (
                  <tr key={challenge._id}>
                    <td className="challenge-title">{challenge.title}</td>
                    <td>
                      <span className={`cyber-badge ${getCategoryBadgeClass(challenge.category)}`}>
                        {challenge.category}
                      </span>
                    </td>
                    <td>
                      <span className={`cyber-badge ${getDifficultyBadgeClass(challenge.difficulty)}`}>
                        {challenge.difficulty}
                      </span>
                    </td>
                    <td className="text-mono">{challenge.totalPoints}</td>
                    <td className="text-mono">{challenge.timeLimit || '-'} min</td>
                    <td>
                      <span className={`cyber-badge ${challenge.isActive ? 'success' : 'danger'}`}>
                        {challenge.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`cyber-badge ${challenge.isAvailable ? 'success' : 'warning'}`}
                        title={challenge.availabilityReason || 'No information'}
                      >
                        {challenge.isAvailable ? 'Available' : 'Unavailable'}
                      </span>
                      {!challenge.isAvailable && challenge.availabilityReason && (
                        <div className="availability-hint">
                          {challenge.availabilityReason}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="cyber-btn-sm"
                          onClick={() => handlePreviewChallenge(challenge)}
                          title="Preview"
                        >
                          View
                        </button>
                        <button
                          className="cyber-btn-sm"
                          onClick={() => handleEditChallenge(challenge)}
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          className="cyber-btn-sm danger"
                          onClick={() => handleDeleteClick(challenge)}
                          title="Delete"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="cyber-btn-sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <div className="page-numbers">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`page-number ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              className="cyber-btn-sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Drawer */}
      <Drawer
        isOpen={showModal && modalMode !== 'preview'}
        onClose={() => setShowModal(false)}
        title={modalMode === 'create' ? 'Create Challenge' : 'Edit Challenge'}
        width="lg"
        footer={
          <>
            <button type="button" className="drawer-btn drawer-btn-secondary" onClick={() => setShowModal(false)}>
              Cancel
            </button>
            <button type="submit" form="challenge-form" className="drawer-btn drawer-btn-primary">
              {modalMode === 'create' ? 'Create Challenge' : 'Update Challenge'}
            </button>
          </>
        }
      >
        <form id="challenge-form" onSubmit={handleSubmit} className="challenge-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    className="cyber-input"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  className="cyber-input"
                  rows="4"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    className="cyber-input"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="web">Web Security</option>
                    <option value="api">API Security</option>
                    <option value="network">Network Security</option>
                    <option value="binary">Binary Exploitation</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Difficulty *</label>
                  <select
                    name="difficulty"
                    className="cyber-input"
                    value={formData.difficulty}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="junior">Junior</option>
                    <option value="senior">Senior</option>
                    <option value="advanced">Advanced</option>
                    <option value="redteam">Red Team</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Total Points *</label>
                  <input
                    type="number"
                    name="totalPoints"
                    className="cyber-input"
                    value={formData.totalPoints}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Time Limit (minutes)</label>
                  <input
                    type="number"
                    name="timeLimit"
                    className="cyber-input"
                    value={formData.timeLimit}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Docker Image</label>
                <input
                  type="text"
                  name="dockerImage"
                  className="cyber-input"
                  value={formData.dockerImage}
                  onChange={handleInputChange}
                  placeholder="e.g., registry.hub.docker.com/challenge:latest"
                />
              </div>

              <div className="form-group">
                <label>Scenario</label>
                <textarea
                  name="scenario"
                  className="cyber-input"
                  rows="6"
                  value={formData.scenario}
                  onChange={handleInputChange}
                  placeholder="Detailed scenario description for the candidate..."
                />
              </div>
            </form>
      </Drawer>

      {/* Preview Drawer */}
      <Drawer
        isOpen={showModal && modalMode === 'preview' && currentChallenge}
        onClose={() => setShowModal(false)}
        title="Challenge Preview"
        width="lg"
        footer={
          <>
            <button className="drawer-btn drawer-btn-secondary" onClick={() => setShowModal(false)}>
              Close
            </button>
            <button
              className="drawer-btn drawer-btn-primary"
              onClick={() => {
                setShowModal(false);
                handleEditChallenge(currentChallenge);
              }}
            >
              Edit Challenge
            </button>
          </>
        }
      >
        {currentChallenge && (
          <div className="preview-content">
              <h3>{currentChallenge.title}</h3>
              <div className="preview-meta">
                <span className={`cyber-badge ${getCategoryBadgeClass(currentChallenge.category)}`}>
                  {currentChallenge.category}
                </span>
                <span className={`cyber-badge ${getDifficultyBadgeClass(currentChallenge.difficulty)}`}>
                  {currentChallenge.difficulty}
                </span>
                <span className="cyber-badge info">{currentChallenge.totalPoints} pts</span>
                {currentChallenge.timeLimit && (
                  <span className="cyber-badge">{currentChallenge.timeLimit} min</span>
                )}
              </div>
              <div className="preview-section">
                <h4>Description</h4>
                <p>{currentChallenge.description}</p>
              </div>
              {currentChallenge.scenario && (
                <div className="preview-section">
                  <h4>Scenario</h4>
                  <p className="scenario-text">{currentChallenge.scenario}</p>
                </div>
              )}
              {currentChallenge.dockerImage && (
                <div className="preview-section">
                  <h4>Docker Image</h4>
                  <code className="text-mono">{currentChallenge.dockerImage}</code>
                </div>
              )}
          </div>
        )}
      </Drawer>

      {/* Delete Confirmation Drawer */}
      <Drawer
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Challenge"
        width="sm"
        footer={
          <>
            <button className="drawer-btn drawer-btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </button>
            <button className="drawer-btn drawer-btn-danger" onClick={handleDeleteConfirm}>
              Delete Challenge
            </button>
          </>
        }
      >
        <div className="confirm-content">
          <p style={{ marginBottom: '1rem', fontSize: '0.95rem', color: '#374151' }}>
            Are you sure you want to delete challenge <strong>"{challengeToDelete?.title}"</strong>?
          </p>
          <p style={{
            padding: '0.75rem',
            background: '#FEF2F2',
            border: '1px solid #FEE2E2',
            borderRadius: '6px',
            color: '#991B1B',
            fontSize: '0.875rem',
            margin: 0
          }}>
            This action cannot be undone.
          </p>
        </div>
      </Drawer>
      </div>
    </div>
  );
});

// Helper functions
const getCategoryBadgeClass = (category) => {
  switch (category) {
    case 'web':
      return 'info';
    case 'api':
      return 'success';
    case 'network':
      return 'warning';
    case 'binary':
      return 'danger';
    case 'mixed':
      return '';
    default:
      return '';
  }
};

const getDifficultyBadgeClass = (difficulty) => {
  switch (difficulty) {
    case 'junior':
      return 'success';
    case 'senior':
      return 'info';
    case 'advanced':
      return 'warning';
    case 'redteam':
      return 'danger';
    default:
      return '';
  }
};

export default ChallengeManagement;
